import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Users, FileText, Calendar, UserCheck, CheckCircle, Briefcase, TrendingUp, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MetricDetailModal from '../components/MetricDetailModal';
import { formatearFechaConDiaCaracas } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [metricas, setMetricas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/analytics/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMetricas(data.metricas);
      } else {
        console.error('Error al cargar métricas');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMetricClick = (metricKey, metricName) => {
    setSelectedMetric({ key: metricKey, name: metricName });
    setShowModal(true);
  };

  const metricCards = [
    {
      key: 'correos_enviados',
      name: 'Correos Enviados',
      icon: Mail,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      key: 'usuarios_nuevos',
      name: 'Usuarios Nuevos',
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      key: 'expedientes_nuevos',
      name: 'Expedientes Nuevos',
      icon: FileText,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      key: 'citas_agendadas',
      name: 'Citas Agendadas',
      icon: Calendar,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      key: 'citas_atendidas',
      name: 'Citas Atendidas',
      icon: Briefcase,
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200'
    },
    {
      key: 'usuarios_retenidos',
      name: 'Usuarios Retenidos',
      icon: UserCheck,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200'
    },
    {
      key: 'retiros_confirmados',
      name: 'Retiros Confirmados',
      icon: CheckCircle,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando métricas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-yellow-500 mb-2">
                📊 Indicadores del Sistema
              </h1>
              <p className="text-gray-400">
                Métricas del día - {formatearFechaConDiaCaracas(new Date())}
              </p>
            </div>
          </div>
        </div>

        {/* Metric Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metricCards.map((metric) => {
            const Icon = metric.icon;
            const value = metricas?.[metric.key] || 0;
            
            return (
              <Card
                key={metric.key}
                className={`bg-slate-900 border-2 ${metric.borderColor} cursor-pointer hover:scale-105 transition-transform duration-200`}
                onClick={() => handleMetricClick(metric.key, metric.name)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg ${metric.bgColor}`}>
                      <Icon className={`w-6 h-6 ${metric.color}`} />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-400">{metric.name}</p>
                    <p className="text-3xl font-bold text-white">{value}</p>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-xs text-gray-500 flex items-center">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Click para ver histórico
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Card */}
        <Card className="bg-slate-900 border-yellow-500 mt-8">
          <CardHeader>
            <CardTitle className="text-yellow-500 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Información
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-400">
              Haz click en cualquier métrica para ver su histórico detallado con gráficas comparativas,
              tendencias semanales y mensuales.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Modal for Metric Details */}
      {showModal && selectedMetric && (
        <MetricDetailModal
          metricKey={selectedMetric.key}
          metricName={selectedMetric.name}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default AnalyticsDashboard;
